/** Back office → Catalogue → service: identity, file steps (instalment triggers), documents. */

import {
  type CategoryEnum,
  type Service,
  type ServiceRequest,
  getCatalogServicesListQueryKey,
  getCatalogServicesRetrieveQueryKey,
  useCatalogServicesCreate,
  useCatalogServicesPartialUpdate,
  useCatalogServicesRetrieve,
} from "@agencyos/api-client";
import { Button, GlassCard, SelectField, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { type Texts, TranslatedFields, asTexts, localized } from "../components/TranslatedFields";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

const CATEGORIES: CategoryEnum[] = ["immigration", "studies", "visa", "travel", "training", "other"];

interface StepRow {
  key: string;
  name: Texts;
  sla: string;
}
interface DocumentRow {
  name: Texts;
  details: Texts;
  required: boolean;
  deadline: string;
  needsConfirmation: boolean;
  note: string;
}

const numberOrNull = (text: string): number | null => (/^\d+$/.test(text.trim()) ? Number(text) : null);

function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
}

function Editor({ service }: { service?: Service }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = useCanEditSettings();
  const [code, setCode] = useState(service?.code ?? "");
  const [name, setName] = useState<Texts>(asTexts(service?.name));
  const [description, setDescription] = useState<Texts>(asTexts(service?.description));
  const [category, setCategory] = useState<CategoryEnum>(service?.category ?? "immigration");
  const [country, setCountry] = useState(service?.country ?? "");
  const [active, setActive] = useState(service?.is_active ?? true);
  const [steps, setSteps] = useState<StepRow[]>(
    (service?.steps ?? []).map((s) => ({ key: s.key, name: asTexts(s.name), sla: s.sla_days?.toString() ?? "" })),
  );
  const [documents, setDocuments] = useState<DocumentRow[]>(
    (service?.documents ?? []).map((d) => ({
      name: asTexts(d.name),
      details: asTexts(d.details),
      required: d.required ?? true,
      deadline: d.deadline_days?.toString() ?? "",
      needsConfirmation: d.needs_confirmation ?? false,
      note: d.confirmation_note ?? "",
    })),
  );
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getCatalogServicesListQueryKey() });
    if (service) void queryClient.invalidateQueries({ queryKey: getCatalogServicesRetrieveQueryKey(service.id) });
  };
  const create = useCatalogServicesCreate();
  const update = useCatalogServicesPartialUpdate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(create.error ?? update.error);

  const move = (index: number, delta: number) =>
    setSteps((rows) => {
      const target = index + delta;
      const moved = rows[index];
      if (moved === undefined || target < 0 || target >= rows.length) return rows;
      const next = rows.filter((_, i) => i !== index);
      next.splice(target, 0, moved);
      return next;
    });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data: ServiceRequest = {
      code,
      name,
      description,
      category,
      country: country.toUpperCase(),
      is_active: active,
      steps: steps.map((row, index) => ({
        key: row.key || slugify(row.name.fr ?? "") || `step_${index + 1}`,
        name: row.name,
        order: index + 1,
        sla_days: numberOrNull(row.sla),
      })),
      documents: documents.map((row, index) => ({
        name: row.name,
        details: row.details,
        required: row.required,
        deadline_days: numberOrNull(row.deadline),
        order: index + 1,
        needs_confirmation: row.needsConfirmation,
        confirmation_note: row.needsConfirmation ? row.note : "",
      })),
    };
    if (service) update.mutate({ id: service.id, data });
    else
      create.mutate(
        { data },
        {
          onSuccess: (response) => {
            refresh();
            if (response.status === 201) navigate(`/catalog/services/${response.data.id}`, { replace: true });
          },
        },
      );
  };
  const setStep = (index: number, patch: Partial<StepRow>) =>
    setSteps((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const setDocument = (index: number, patch: Partial<DocumentRow>) =>
    setDocuments((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <form onSubmit={onSubmit} className="stack" noValidate>
      <GlassCard className="stack editor-head">
        <Link to="/catalog" className="link">
          ← {t("common.back")}
        </Link>
        <h1>{service ? localized(service.name, i18n.language) : t("catalog.newService")}</h1>
      </GlassCard>
      <fieldset className="plain-fieldset service-editor" disabled={!canEdit}>
        <GlassCard as="section" className="stack">
          <h2 className="ao-card__title">{t("catalog.sections.identity")}</h2>
          <TextField
            label={t("catalog.fields.code")}
            value={code}
            hint={t("catalog.codeHint")}
            maxLength={20}
            error={errors.code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <TranslatedFields label={t("catalog.fields.name")} value={name} error={errors.name} onChange={setName} />
          <TranslatedFields label={t("catalog.fields.description")} value={description} multiline required={false} onChange={setDescription} />
          <SelectField
            label={t("catalog.fields.category")}
            value={category}
            options={CATEGORIES.map((c) => ({ value: c, label: t(`catalog.category.${c}`) }))}
            onChange={(e) => setCategory(e.target.value as CategoryEnum)}
          />
          <TextField label={t("catalog.fields.country")} value={country} maxLength={2} onChange={(e) => setCountry(e.target.value)} />
          <SoftToggle label={t("catalog.fields.active")} checked={active} onChange={setActive} />
        </GlassCard>

        <GlassCard as="section" className="stack">
          <h2>{t("catalog.steps")}</h2>
          {steps.map((row, index) => (
            <div key={index} className="installment-row stack">
              <TranslatedFields label={t("catalog.fields.stepName")} value={row.name} onChange={(v) => setStep(index, { name: v })} />
              <div className="grid-2">
                <TextField
                  label={t("catalog.fields.stepKey")}
                  value={row.key}
                  placeholder={slugify(row.name.fr ?? "")}
                  onChange={(e) => setStep(index, { key: slugify(e.target.value) })}
                />
                <TextField
                  label={t("catalog.fields.sla")}
                  inputMode="numeric"
                  value={row.sla}
                  onChange={(e) => setStep(index, { sla: e.target.value })}
                />
              </div>
              <div className="row">
                <Button variant="ghost" onClick={() => move(index, -1)} disabled={index === 0}>
                  {t("common.moveUp")}
                </Button>
                <Button variant="ghost" onClick={() => move(index, 1)} disabled={index === steps.length - 1}>
                  {t("common.moveDown")}
                </Button>
                <Button variant="ghost" onClick={() => setSteps((rows) => rows.filter((_, i) => i !== index))}>
                  {t("common.remove")}
                </Button>
              </div>
            </div>
          ))}
          {errors.steps ? <p role="alert" className="ao-field__error">{errors.steps}</p> : null}
          <div className="row">
            <Button variant="secondary" onClick={() => setSteps((rows) => [...rows, { key: "", name: {}, sla: "" }])}>
              {t("catalog.addStep")}
            </Button>
          </div>
        </GlassCard>

        <GlassCard as="section" className="stack">
          <h2>{t("catalog.documents")}</h2>
          {documents.map((row, index) => (
            <div key={index} className="installment-row stack">
              <TranslatedFields label={t("catalog.fields.document")} value={row.name} onChange={(v) => setDocument(index, { name: v })} />
              <TranslatedFields
                label={t("catalog.fields.details")}
                value={row.details}
                required={false}
                onChange={(v) => setDocument(index, { details: v })}
              />
              <TextField
                label={t("catalog.fields.deadline")}
                inputMode="numeric"
                value={row.deadline}
                onChange={(e) => setDocument(index, { deadline: e.target.value })}
              />
              <div className="row">
                <SoftToggle label={t("catalog.fields.required")} checked={row.required} onChange={(v) => setDocument(index, { required: v })} />
                <SoftToggle
                  label={t("catalog.needsConfirmation")}
                  checked={row.needsConfirmation}
                  onChange={(v) => setDocument(index, { needsConfirmation: v })}
                />
                <Button variant="ghost" onClick={() => setDocuments((rows) => rows.filter((_, i) => i !== index))}>
                  {t("common.remove")}
                </Button>
              </div>
              {row.needsConfirmation ? (
                <TextField
                  label={t("catalog.confirmationNote")}
                  value={row.note}
                  maxLength={300}
                  onChange={(e) => setDocument(index, { note: e.target.value })}
                />
              ) : null}
            </div>
          ))}
          <div className="row">
            <Button
              variant="secondary"
              onClick={() =>
                setDocuments((rows) => [
                  ...rows,
                  { name: {}, details: {}, required: true, deadline: "", needsConfirmation: false, note: "" },
                ])
              }
            >
              {t("catalog.addDocument")}
            </Button>
          </div>
        </GlassCard>
      </fieldset>
      {canEdit ? (
        <GlassCard className="row sticky-actions">
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {create.isPending || update.isPending ? t("common.loading") : t("catalog.save")}
          </Button>
          <p role="status" className={Object.keys(errors).length ? "ko" : "ok"}>
            {Object.keys(errors).length ? t("common.fixErrors") : update.isSuccess ? t("common.saved") : ""}
          </p>
        </GlassCard>
      ) : null}
    </form>
  );
}

export function ServiceEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const service = useCatalogServicesRetrieve(id ?? "", { query: { enabled: !isNew } });
  let content;
  if (isNew) content = <Editor />;
  else if (service.isPending)
    content = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (service.data?.status !== 200) content = <GlassCard role="alert">{t("common.error")}</GlassCard>;
  else content = <Editor key={service.data.data.id} service={service.data.data} />;
  return <Page>{content}</Page>;
}
